import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { C, Btn, Card, SectionTitle } from "../components/UI";

const CRITERIA = [
  "Igualdade salarial",
  "Liderança feminina",
  "Política de maternidade",
  "Ambiente de trabalho",
  "Crescimento de carreira",
  "Flexibilidade",
];

const PROMISES = [
  { title: "Anônimo por padrão", desc: "Você escolhe o que aparece. Seu nome não é publicado." },
  { title: "Toda avaliação é moderada", desc: "Analisamos antes de publicar, para manter o respeito." },
  { title: "Gratuito para mulheres", desc: "Avaliar e pesquisar não custa nada, hoje e sempre." },
];

const HOW = [
  { num: "01", title: "Cadastre-se grátis", desc: "Crie sua conta em segundos. Sua identidade é protegida — você escolhe o que compartilhar." },
  { num: "02", title: "Avalie sua experiência", desc: "Nota geral, ambiente, liderança, salário e maternidade. Critérios que importam pra nós." },
  { num: "03", title: "Tome decisões melhores", desc: "Leia avaliações antes de aceitar uma proposta. Filtre por setor, cidade e porte." },
];

export default function Home() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div>
      {/* HERO */}
      <section style={{
        background: `linear-gradient(135deg, ${C.rosePale} 0%, #faf9f7 55%)`,
        padding: isMobile ? "40px 20px" : isTablet ? "50px 40px" : "72px 80px 56px",
        display: "grid",
        gridTemplateColumns: isMobile || isTablet ? "1fr" : "1fr 1fr",
        gap: isMobile ? 32 : 56,
        alignItems: "center"
      }}>
        <div className="anim-1">
          <div style={{
            display: "inline-block",
            background: C.rosePale,
            color: C.roseMid,
            border: `0.5px solid ${C.roseBorder}`,
            borderRadius: 100,
            padding: "5px 14px",
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: "1.2px",
            textTransform: "uppercase",
            marginBottom: 20
          }}>
            Mercado de trabalho mais justo
          </div>
          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: isMobile ? 32 : isTablet ? 38 : 46,
            fontWeight: 700,
            lineHeight: 1.15,
            marginBottom: 18
          }}>
            O que <em style={{ fontStyle: "italic", color: C.roseMid }}>elas</em> dizem<br />sobre onde trabalham.
          </h1>
          <p style={{
            fontSize: isMobile ? 14 : 16,
            color: C.textSec,
            lineHeight: 1.75,
            marginBottom: 32,
            maxWidth: 440
          }}>
            Avaliações reais de mulheres reais. Descubra se uma empresa realmente valoriza e respeita quem trabalha lá — antes de aceitar a vaga.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Btn onClick={() => navigate("/cadastro")}>Avaliar uma empresa</Btn>
            <Btn variant="secondary" onClick={() => navigate("/empresas")}>Ver empresas</Btn>
          </div>
        </div>

        {/* O que se avalia */}
        {!isMobile && (
          <div className="anim-2">
            <Card style={{ border: `1px solid ${C.roseBorder}`, background: C.rosePale, padding: "28px 26px" }}>
              <div style={{
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "1.2px",
                textTransform: "uppercase",
                color: C.roseMid,
                marginBottom: 16
              }}>
                O que você avalia
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 20px" }}>
                {CRITERIA.map(c => (
                  <div key={c} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <span style={{ color: C.roseMid, fontSize: 13, flexShrink: 0 }}>★</span>
                    <span style={{ fontSize: 13, color: C.textSec, lineHeight: 1.5 }}>{c}</span>
                  </div>
                ))}
              </div>
              <p style={{
                fontSize: 12,
                color: C.textSec,
                lineHeight: 1.6,
                marginTop: 20,
                paddingTop: 16,
                borderTop: `0.5px solid ${C.roseBorder}`
              }}>
                Cinco minutos do seu tempo podem mudar a decisão de carreira de outra mulher.
              </p>
            </Card>
          </div>
        )}
      </section>

      {/* COMPROMISSOS */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)",
        borderTop: `0.5px solid ${C.border}`,
        borderBottom: `0.5px solid ${C.border}`
      }}>
        {PROMISES.map((p, i) => (
          <div key={p.title} style={{
            padding: isMobile ? "20px" : "26px 24px",
            textAlign: "center",
            borderRight: !isMobile && i < 2 ? `0.5px solid ${C.border}` : "none",
            borderBottom: isMobile && i < 2 ? `0.5px solid ${C.border}` : "none"
          }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: C.roseMid, marginBottom: 6 }}>{p.title}</div>
            <div style={{ fontSize: 13, color: C.textSec, lineHeight: 1.6 }}>{p.desc}</div>
          </div>
        ))}
      </div>

      {/* COMO FUNCIONA */}
      <section id="como-funciona" style={{ padding: isMobile ? "40px 20px" : isTablet ? "50px 40px" : "72px 80px" }}>
        <SectionTitle tag="Como funciona" title="Simples, seguro e feito por mulheres" sub="Para mulheres que avaliam e para aquelas que buscam um lugar onde possam crescer." center />
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(3,1fr)",
          gap: isMobile ? 16 : 24
        }}>
          {HOW.map((h) => (
            <Card key={h.num} hover style={{ padding: isMobile ? "20px" : "28px 24px" }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 32 : 40, fontWeight: 700, color: C.roseBorder, marginBottom: 14 }}>{h.num}</div>
              <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>{h.title}</h3>
              <p style={{ fontSize: 14, color: C.textSec, lineHeight: 1.7 }}>{h.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* CONVITE */}
      <section style={{ padding: isMobile ? "0 20px 40px" : isTablet ? "0 40px 50px" : "0 80px 72px" }}>
        <Card style={{
          padding: isMobile ? "32px 24px" : "48px 40px",
          textAlign: "center",
          border: `1px solid ${C.roseBorder}`
        }}>
          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: isMobile ? 24 : 30,
            fontWeight: 700,
            marginBottom: 12
          }}>
            A primeira avaliação pode ser a sua
          </h2>
          <p style={{
            fontSize: isMobile ? 14 : 15,
            color: C.textSec,
            lineHeight: 1.75,
            maxWidth: 520,
            margin: "0 auto 28px"
          }}>
            A WoMom está começando agora. Cada experiência compartilhada ajuda a próxima mulher a saber onde está pisando antes de aceitar uma vaga.
          </p>
          <Btn onClick={() => navigate("/cadastro")}>Compartilhar minha experiência</Btn>
        </Card>
      </section>

      {/* CTA EMPRESAS */}
      <section id="empresas" style={{
        background: C.roseMid,
        padding: isMobile ? "40px 20px" : isTablet ? "50px 40px" : "64px 80px",
        textAlign: "center"
      }}>
        <div style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: isMobile ? 24 : isTablet ? 28 : 32,
          fontWeight: 700,
          color: "#fff",
          marginBottom: 12
        }}>
          Sua empresa é um lugar onde mulheres querem trabalhar?
        </div>
        <p style={{ fontSize: isMobile ? 14 : 15, color: C.roseBorder, marginBottom: 28 }}>
          Crie seu perfil, responda avaliações e publique vagas para talentos femininos.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Btn variant="white" onClick={() => navigate("/empresas?tab=planos")}>Ver planos para empresas</Btn>
          <Btn onClick={() => navigate("/cadastro-empresa")} style={{ background: "transparent", color: "#fff", border: "1.5px solid rgba(255,255,255,0.4)" }}>Criar perfil grátis</Btn>
        </div>
      </section>

    </div>
  );
}
